import { QUESTIONS, SECTIONS } from './questions.js';

const iconCheck = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
const fallbackRegex = /(none of these|none \/|none of the above|my child does not|standard classroom|standard text|standard writing|standard direct|handled independently|mix of generic)/i;
const phases = ['green', 'amber', 'blue', 'green', 'amber', 'blue'];
const headers = { basics: 'Add baseline details so the handbook is accurate and role-tuned.', about: 'Describe strengths, passions, and how your child expresses their experience.', adhd: 'Choose condition areas in Step 1 to unlock focused profile questions.', strategies: 'Share what helps with instructions, transitions, and reinforcement.', schoolday: 'Detail classroom bottlenecks, breaks, and subject patterns.', team: 'Set emergency, specialist, and feedback preferences.' };

const hasValue = (value) => value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : String(value).trim() !== '');
const activeQuestions = (state) => QUESTIONS.filter((question) => question.section === SECTIONS[state.currentStep]?.id && (!question.condition || question.condition(state.answers)));

export function buildSidebarNavigation({ state, elements, goToStep }) {
  elements.stepsNavigation.innerHTML = '';
  SECTIONS.forEach((section, index) => {
    const item = document.createElement('li');
    item.className = 'step-item'; item.id = `step-nav-${index}`; item.dataset.phase = phases[index] || 'green';
    item.innerHTML = `<span class="step-number">${index + 1}</span><span class="step-label">${section.title.replace(/^\d+ — /, '')}</span>`;
    item.addEventListener('click', () => state.currentStep >= 0 && goToStep(index));
    elements.stepsNavigation.appendChild(item);
  });
}

export function goToStep(step, ctx) {
  ctx.state.currentStep = step;
  ctx.state.screenIntent = step >= SECTIONS.length ? 'review' : 'wizard';
  ctx.saveStateToStorage();
  ctx.routeScreen();
  (ctx.elements.formContainer || document.body).scrollTop = 0;
}

export function handleNextButton(ctx) {
  const invalid = activeQuestions(ctx.state).find((question) => question.required && !hasValue(ctx.state.answers[question.id]));
  if (invalid) {
    document.getElementById(`q-container-${invalid.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    alert('Please complete the required questions before moving to the next section.');
    return;
  }
  if (ctx.state.currentStep === SECTIONS.length - 1) {
    goToStep(SECTIONS.length, ctx);
    return;
  }
  goToStep(ctx.state.currentStep + 1, ctx);
}

function onInput(ctx, questionId, value) {
  ctx.state.answers[questionId] = value;
  if (questionId === 'takesMedication' && value === 'No') delete ctx.state.answers.medicationRole;
  if (questionId === 'condition' && !(value || []).some((item) => item.includes('Other diagnosed learning'))) delete ctx.state.answers.additionalProfiles;
  ctx.saveStateToStorage(); ctx.updatePreview(); calculateAndRenderProgress(ctx);
}

function renderOptionCard({ type, name, selected, label, onChange }) {
  const card = document.createElement('label');
  card.className = `option-card ${selected ? 'selected' : ''}`;
  card.innerHTML = `<input type="${type}" ${type === 'radio' ? `name="${name}"` : ''} value="${label}" ${selected ? 'checked' : ''}/><span class="option-label">${label}</span>`;
  card.querySelector('input').addEventListener('change', onChange);
  return card;
}

function renderQuestion(ctx, sectionId, question) {
  const box = document.createElement('div');
  box.className = 'question-group'; box.id = `q-container-${question.id}`;
  box.innerHTML = `<h3 class="question-title ${question.required ? 'required-dot' : ''}">${question.question}</h3>${question.hint ? `<p class="question-hint">${question.hint}</p>` : ''}`;
  const value = ctx.state.answers[question.id];
  if (question.type === 'text' || question.type === 'textarea') {
    const input = document.createElement(question.type === 'text' ? 'input' : 'textarea');
    input.className = question.type === 'text' ? 'input-text' : 'input-textarea';
    input.placeholder = question.placeholder || ''; input.value = value || ''; input.disabled = Boolean(ctx.state.readOnly);
    input.addEventListener('input', (event) => onInput(ctx, question.id, event.target.value)); box.appendChild(input); return box;
  }
  const grid = document.createElement('div'); grid.className = 'options-grid';
  const selected = Array.isArray(value) ? value : [];
  question.options.forEach((option) => {
    const isMulti = question.type === 'multi-select';
    const card = renderOptionCard({
      type: isMulti ? 'checkbox' : 'radio',
      name: `q-group-${question.id}`,
      selected: isMulti ? selected.includes(option) : value === option,
      label: option,
      onChange: () => {
        if (ctx.state.readOnly) return;
        if (!isMulti) return onInput(ctx, question.id, option), renderActiveStepQuestions(ctx);
        let next = [...selected];
        if (next.includes(option)) next = next.filter((item) => item !== option);
        else next = fallbackRegex.test(option) ? [option] : [...next.filter((item) => !fallbackRegex.test(item)), option];
        if (question.id === 'strengths' && next.length > 4) return alert('You can select a maximum of 4 strengths.');
        onInput(ctx, question.id, next); renderActiveStepQuestions(ctx);
      },
    });
    grid.appendChild(card);
  });
  box.appendChild(grid); return box;
}

function renderActiveStepQuestions(ctx) {
  const section = SECTIONS[ctx.state.currentStep]; if (!section) return;
  ctx.elements.questionsViewport.innerHTML = `<div class="form-header phase-${phases[ctx.state.currentStep] || 'green'}"><h2>${section.title}</h2><p>${headers[section.id] || ''}</p></div>`;
  activeQuestions(ctx.state).forEach((question) => ctx.elements.questionsViewport.appendChild(renderQuestion(ctx, section.id, question)));
}

export function updateWizardUI(ctx) {
  ['welcomeStep', 'handbookForm'].forEach((key) => { ctx.elements[key].style.display = 'none'; });
  [...ctx.elements.stepsNavigation.querySelectorAll('.step-item')].forEach((item) => item.classList.remove('active'));
  if (ctx.state.currentStep === -1) return updateProgressRing(ctx.elements, 0), void (ctx.elements.welcomeStep.style.display = 'flex');
  ctx.elements.handbookForm.style.display = 'flex'; document.getElementById(`step-nav-${ctx.state.currentStep}`)?.classList.add('active');
  ctx.elements.btnWizardBack.style.visibility = ctx.state.currentStep === 0 ? 'hidden' : 'visible';
  ctx.elements.btnWizardBack.disabled = Boolean(ctx.state.readOnly);
  ctx.elements.btnWizardNext.disabled = Boolean(ctx.state.readOnly);
  ctx.elements.btnWizardNext.textContent = ctx.state.currentStep === SECTIONS.length - 1 ? 'Review draft' : 'Next';
  renderActiveStepQuestions(ctx); calculateAndRenderProgress(ctx);
}

export function calculateAndRenderProgress(ctx) {
  let required = 0; let answered = 0;
  SECTIONS.forEach((section, index) => {
    const sectionQuestions = QUESTIONS.filter((question) => question.section === section.id && (!question.condition || question.condition(ctx.state.answers)) && question.required);
    const sectionAnswered = sectionQuestions.filter((question) => hasValue(ctx.state.answers[question.id])).length;
    required += sectionQuestions.length; answered += sectionAnswered;
    const item = document.getElementById(`step-nav-${index}`); const number = item?.querySelector('.step-number');
    item?.classList.toggle('completed', sectionQuestions.length > 0 && sectionAnswered === sectionQuestions.length);
    if (number) number.innerHTML = item?.classList.contains('completed') ? iconCheck : String(index + 1);
  });
  updateProgressRing(ctx.elements, required ? Math.round((answered / required) * 100) : 0);
}

function updateProgressRing(elements, percent) {
  const radius = elements.progressCircle.r.baseVal.value;
  const circumference = 2 * Math.PI * radius;
  elements.progressCircle.style.strokeDashoffset = String(circumference - (percent / 100) * circumference);
  elements.progressText.textContent = `${percent}%`;
}

export function isQuestionnaireComplete(state) {
  return QUESTIONS.filter((question) => (!question.condition || question.condition(state.answers)) && question.required)
    .every((question) => hasValue(state.answers[question.id]));
}
